import { useEffect, useState } from "react";
import { Mail, MailOpen, Trash2, Loader2, Reply, Send, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiErr } from "@/lib/api";
import { Field } from "./ImageField";

const formatDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const InboxTab = () => {
  const [messages, setMessages] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [subject, setSubject] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const openReply = (msg) => {
    setReplyTo(msg);
    setSubject(`Re: Your enquiry to Sharma Model Aero`);
    setReplyText("");
  };

  const sendReply = async () => {
    if (replyText.trim().length < 2) {
      toast.error("Please write a reply message");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/contact/${replyTo.id}/reply`, {
        subject,
        message: replyText,
      });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === replyTo.id
            ? { ...m, replied: true, read: true, replies: [...(m.replies || []), data.reply] }
            : m
        )
      );
      setReplyTo(null);
      toast.success(`Reply sent to ${replyTo.email}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSending(false);
    }
  };

  const load = () =>
    api
      .get("/contact")
      .then((r) => setMessages(r.data))
      .catch((e) => toast.error(apiErr(e)));

  useEffect(() => {
    load();
  }, []);

  const toggleRead = async (msg) => {
    try {
      await api.patch(`/contact/${msg.id}`, { read: !msg.read });
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, read: !m.read } : m)));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contact/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setConfirmDelete(null);
      toast.success("Message deleted");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (!messages)
    return <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin text-saffron" />;

  const unread = messages.filter((m) => !m.read).length;

  return (
    <div data-testid="admin-inbox">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-ink">Contact Inbox</h1>
        <p className="mt-1 font-body text-sm text-steel">
          {messages.length} message{messages.length !== 1 && "s"} · <span className="text-saffron">{unread} unread</span>
        </p>
      </div>

      {messages.length === 0 && (
        <p className="rounded-xl border border-steel/20 bg-aero-surface p-8 text-center font-body text-sm text-steel">
          No enquiries yet.
        </p>
      )}

      <div className="space-y-3">
        {messages.map((m) => (
          <div
            key={m.id}
            data-testid={`inbox-message-${m.id}`}
            className={`rounded-xl border p-5 transition-colors ${
              m.read ? "border-steel/15 bg-aero-surface/60" : "border-saffron/40 bg-aero-surface"
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-heading text-sm font-bold text-ink">
                  {m.name}
                  {!m.read && (
                    <span className="ml-2 rounded-full bg-saffron px-2 py-0.5 font-heading text-[10px] font-semibold uppercase text-white">
                      New
                    </span>
                  )}
                  {m.replied && (
                    <span data-testid={`inbox-replied-badge-${m.id}`} className="ml-2 inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 font-heading text-[10px] font-semibold uppercase text-green-700">
                      <CheckCheck className="h-3 w-3" /> Replied
                    </span>
                  )}
                </p>
                <a href={`mailto:${m.email}`} className="font-body text-xs text-saffron hover:underline">
                  {m.email}
                </a>
                <p className="mt-0.5 font-body text-[11px] text-steel/70">{formatDate(m.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`inbox-reply-${m.id}`}
                  onClick={() => openReply(m)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-saffron px-3 py-1.5 font-heading text-xs font-semibold text-white transition-colors hover:bg-saffron-soft"
                >
                  <Reply className="h-3.5 w-3.5" /> Reply
                </button>
                <button
                  data-testid={`inbox-toggle-read-${m.id}`}
                  onClick={() => toggleRead(m)}
                  title={m.read ? "Mark as unread" : "Mark as read"}
                  className="inline-flex items-center gap-1.5 rounded-full border border-steel/25 px-3 py-1.5 font-heading text-xs font-semibold text-steel transition-colors hover:border-saffron hover:text-saffron"
                >
                  {m.read ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
                  {m.read ? "Mark unread" : "Mark read"}
                </button>
                {confirmDelete === m.id ? (
                  <button
                    data-testid={`inbox-confirm-delete-${m.id}`}
                    onClick={() => remove(m.id)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 font-heading text-xs font-semibold text-white"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Confirm?
                  </button>
                ) : (
                  <button
                    data-testid={`inbox-delete-${m.id}`}
                    onClick={() => setConfirmDelete(m.id)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 font-heading text-xs font-semibold text-red-500 transition-colors hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                )}
              </div>
            </div>
            <p className="mt-3 whitespace-pre-wrap border-t border-steel/15 pt-3 font-body text-sm leading-relaxed text-ink">
              {m.message}
            </p>
            {(m.replies || []).length > 0 && (
              <div className="mt-3 space-y-2">
                {m.replies.map((r) => (
                  <div key={r.sent_at} className="rounded-lg border-l-2 border-saffron bg-white/70 px-4 py-3">
                    <p className="font-heading text-[11px] font-semibold uppercase tracking-wide text-saffron">
                      Your reply · {formatDate(r.sent_at)}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap font-body text-xs leading-relaxed text-steel">{r.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Reply dialog */}
      <Dialog open={!!replyTo} onOpenChange={(o) => !o && setReplyTo(null)}>
        <DialogContent data-testid="inbox-reply-dialog" className="max-w-xl border-steel/25 bg-aero-surface text-ink">
          {replyTo && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold text-ink">
                  Reply to {replyTo.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="rounded-lg bg-white/70 px-4 py-3 font-body text-xs leading-relaxed text-steel">
                  <span className="font-semibold text-ink">To:</span> {replyTo.email}
                  <br />
                  <span className="font-semibold text-ink">Their message:</span> {replyTo.message}
                </p>
                <Field label="Subject" value={subject} testId="inbox-reply-subject" onChange={setSubject} />
                <Field label="Your reply" textarea value={replyText} testId="inbox-reply-message" onChange={setReplyText} />
                <button
                  data-testid="inbox-reply-send-btn"
                  onClick={sendReply}
                  disabled={sending}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-saffron py-3 font-heading text-sm font-semibold text-white transition-colors hover:bg-saffron-soft disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {sending ? "Sending…" : "Send Reply"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
